import type {
  AutoReplyReport,
  AutoReplySkipReason,
  AutoReplyTopicSetting,
  AutoReplyTopicStats,
} from '@sbc/domain-models/gateway';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAutoReplyReport,
  useUpdateAutoReplyGlobal,
  useUpdateAutoReplyTopic,
} from '@/lib/api/draft-shadow';
import { share } from '@/lib/utils';

export const MAX_DAILY_CAP = 5000;

export const SKIP_REASON_LABELS: Record<AutoReplySkipReason, string> = {
  'switched-off': 'Switched off while sending',
  'thread-unreadable': 'Could not read the conversation',
  'forwarded-by-staff': 'Forwarded by a teammate',
  'no-member-message': 'No member message to answer',
  'member-wrote-again': 'Member wrote again',
  'teammate-replied': 'A teammate already replied',
  'not-open': 'Conversation not open',
  'teammate-replied-recently': 'A teammate replied in the last day',
  'conversation-limit': 'Already auto-replied in the last day',
  'daily-cap': 'Daily cap reached',
  'sub-intent-off': 'Sub-intent not switched on',
};

export interface AutoReplyTopicView {
  topic: string;
  setting: AutoReplyTopicSetting | null;
  stats: AutoReplyTopicStats | null;
}

export function autoReplyTopicRows(
  report: Pick<AutoReplyReport, 'settings' | 'topics'>,
): AutoReplyTopicView[] {
  const stats = new Map(report.topics.map((row) => [row.topic, row]));
  const known = new Set<string>(report.settings.topics.map((setting) => setting.topic));
  return [
    ...report.settings.topics.map((setting) => ({
      topic: setting.topic,
      setting,
      stats: stats.get(setting.topic) ?? null,
    })),
    ...report.topics
      .filter((row) => !known.has(row.topic))
      .map((row) => ({ topic: row.topic, setting: null, stats: row })),
  ];
}

export function parseDailyCap(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) {
    return null;
  }
  const cap = Number(value);
  return cap <= MAX_DAILY_CAP ? cap : null;
}

export function parseTeamId(value: string): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  return /^\d{1,20}$/.test(trimmed) ? trimmed : undefined;
}

export function AutoReplyPanel({ days }: { days: number }) {
  const reportQuery = useAutoReplyReport(days);
  const updateGlobal = useUpdateAutoReplyGlobal();
  const updateTopic = useUpdateAutoReplyTopic();
  const [capInput, setCapInput] = useState<string | null>(null);
  const [teamInput, setTeamInput] = useState<string | null>(null);
  const report = reportQuery.data;

  if (reportQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load auto-send: {reportQuery.error.message}
      </p>
    );
  }
  if (!report) {
    return <p className="text-sm">Loading auto-send…</p>;
  }

  const { settings } = report;
  const cap = capInput ?? String(settings.dailyCap);
  const parsedCap = parseDailyCap(cap);
  const team = teamInput ?? settings.csTaskTeamId ?? '';
  const parsedTeam = parseTeamId(team);
  const saving = updateGlobal.isPending || updateTopic.isPending;
  const saveError = updateGlobal.error ?? updateTopic.error;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Auto-send</h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          When auto-send and a topic are on, a draft that passes every check goes to the member as
          the reply, tagged and closed. Anything else gets the usual note for CS.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-6 rounded-md border p-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4"
            checked={settings.enabled}
            disabled={saving}
            onChange={(event) => updateGlobal.mutate({ enabled: event.target.checked })}
          />
          Auto-send on
          {settings.enabled ? <Badge>On</Badge> : <Badge variant="outline">Off</Badge>}
        </label>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="auto-reply-daily-cap">Daily cap</Label>
            <Input
              id="auto-reply-daily-cap"
              type="number"
              min={0}
              max={MAX_DAILY_CAP}
              className="w-28"
              value={cap}
              onChange={(event) => setCapInput(event.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={saving || parsedCap === null || parsedCap === settings.dailyCap}
            onClick={() =>
              parsedCap !== null &&
              updateGlobal.mutate({ dailyCap: parsedCap }, { onSuccess: () => setCapInput(null) })
            }
          >
            Save cap
          </Button>
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          At most {settings.dailyCap} auto-replies in any 24 hours, and one per conversation.
        </p>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="auto-reply-cs-task-team">CS task team id</Label>
            <Input
              id="auto-reply-cs-task-team"
              inputMode="numeric"
              className="w-36"
              placeholder="None"
              value={team}
              onChange={(event) => setTeamInput(event.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={saving || parsedTeam === undefined || parsedTeam === settings.csTaskTeamId}
            onClick={() =>
              parsedTeam !== undefined &&
              updateGlobal.mutate(
                { csTaskTeamId: parsedTeam },
                { onSuccess: () => setTeamInput(null) },
              )
            }
          >
            Save team
          </Button>
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          A reply sent with a CS task left to do stays open with the task as a note, assigned to
          this Intercom team. Empty leaves it unassigned.
        </p>
      </div>

      {saveError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Could not save: {saveError.message}
        </p>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Topic</TableHead>
              <TableHead>On</TableHead>
              <TableHead className="text-right">Sent</TableHead>
              <TableHead className="text-right">Judged right</TableHead>
              <TableHead className="text-right">Wrote back in 3 days</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead className="text-right">Failed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {autoReplyTopicRows(report).map((row) => (
              <TableRow key={row.topic}>
                <TableCell className="font-medium">{row.topic}</TableCell>
                <TableCell>
                  {row.setting && (
                    <input
                      type="checkbox"
                      className="size-4"
                      aria-label={`Auto-send ${row.topic}`}
                      checked={row.setting.enabled}
                      disabled={saving}
                      onChange={(event) =>
                        row.setting &&
                        updateTopic.mutate({
                          topic: row.setting.topic,
                          enabled: event.target.checked,
                        })
                      }
                    />
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{row.stats?.sent ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {share(row.stats?.judgedRight ?? 0, row.stats?.graded ?? 0)}
                  <span className="text-xs text-[var(--color-muted-foreground)]">
                    {' '}
                    of {row.stats?.graded ?? 0}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {share(row.stats?.memberWroteBack ?? 0, row.stats?.watched ?? 0)}
                  <span className="text-xs text-[var(--color-muted-foreground)]">
                    {' '}
                    of {row.stats?.watched ?? 0}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{row.stats?.skipped ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{row.stats?.failed ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {report.skips.length > 0 && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Skipped:{' '}
          {report.skips
            .map((skip) => `${SKIP_REASON_LABELS[skip.reason]} (${skip.count})`)
            .join(' · ')}
        </p>
      )}

      <div className="space-y-2">
        <h3 className="text-base font-semibold tracking-tight">Auto-sent replies</h3>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead>Judged</TableHead>
                <TableHead>Member wrote back</TableHead>
                <TableHead className="w-1/3">Note</TableHead>
                <TableHead>Conversation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.replies.length ? (
                report.replies.map((reply) => (
                  <TableRow key={reply.id}>
                    <TableCell className="text-sm">
                      {new Date(reply.sentAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm">{reply.topic}</TableCell>
                    <TableCell>
                      {reply.judgedRight === null ? (
                        <Badge variant="outline">Not yet</Badge>
                      ) : reply.judgedRight ? (
                        <Badge>Right</Badge>
                      ) : (
                        <Badge variant="destructive">Wrong</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {reply.memberRepliedAt
                        ? new Date(reply.memberRepliedAt).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {[reply.judgedRight === false ? reply.judgeReason : null, reply.error]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </TableCell>
                    <TableCell>
                      <a
                        href={reply.conversationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium hover:underline"
                      >
                        Open in Intercom
                      </a>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    No auto-sent replies in this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
